import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { categoryService } from '../../../services/categoryService';
import { accountService } from '../../../services/accountService';

const SplitTransactionModal = ({ transaction, onSubmit, onClose }) => {
  const [splits, setSplits] = useState([
    { category: '', subcategory: '', amount: '', account: transaction?.accountId || '', editing: false },
    { category: '', subcategory: '', amount: '', account: transaction?.accountId || '', editing: false }
  ]);
  const [parentCategories, setParentCategories] = useState([]);
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [savingIndex, setSavingIndex] = useState(null);

  useEffect(() => {
    loadParentCategories();
    loadAccounts();
  }, []);

  const loadParentCategories = async () => {
    try {
      const data = await categoryService?.getParentCategories(transaction?.type);
      setParentCategories(data || []);
    } catch (error) {
      console.error('Error loading parent categories:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService?.getAll();
      setAccounts(data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadSubcategories = async (parentId, splitIndex) => {
    try {
      const data = await categoryService?.getSubcategories(parentId);
      setSubcategoriesMap(prev => ({
        ...prev,
        [splitIndex]: data || []
      }));
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const categoryOptions = [
    { value: '', label: 'Select Category' },
    ...parentCategories?.map(cat => ({ value: cat?.id, label: cat?.name }))
  ];

  const getSubcategoryOptions = (splitIndex) => {
    const subcats = subcategoriesMap?.[splitIndex] || [];
    return [
      { value: '', label: 'None' },
      ...subcats?.map(cat => ({ value: cat?.id, label: cat?.name }))
    ];
  };

  const accountOptions = [
    { value: '', label: 'Select Account' },
    ...accounts?.map(acc => ({ value: acc?.id, label: acc?.name }))
  ];

  const totalAmount = Math.abs(transaction?.amount || 0);
  const allocatedAmount = splits?.reduce((sum, split) => sum + (parseFloat(split?.amount) || 0), 0);
  const remainingAmount = totalAmount - allocatedAmount;
  const isValid = Math.abs(remainingAmount) < 0.01 && splits?.every(s => s?.category && s?.amount && s?.account);

  const handleAddSplit = () => {
    setSplits([...splits, { category: '', subcategory: '', amount: '', account: transaction?.accountId || '', editing: true }]);
  };

  const handleRemoveSplit = (index) => {
    if (splits?.length > 2) {
      setSplits(splits?.filter((_, i) => i !== index));
      const newMap = { ...subcategoriesMap };
      delete newMap?.[index];
      setSubcategoriesMap(newMap);
    }
  };

  const handleSplitChange = (index, field, value) => {
    const newSplits = [...splits];
    newSplits[index][field] = value;
    
    if (field === 'category') {
      newSplits[index].subcategory = '';
      if (value) {
        loadSubcategories(value, index);
      } else {
        const newMap = { ...subcategoriesMap };
        delete newMap?.[index];
        setSubcategoriesMap(newMap);
      }
    }
    
    setSplits(newSplits);
  };

  const handleEditSplit = (index) => {
    const newSplits = [...splits];
    newSplits[index].editing = true;
    setSplits(newSplits);
  };

  const handleSaveSplit = async (index) => {
    const split = splits?.[index];
    if (!split?.category || !split?.amount || !split?.account) {
      return;
    }
    
    setSavingIndex(index);
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newSplits = [...splits];
    newSplits[index].editing = false;
    setSplits(newSplits);
    setSavingIndex(null);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (isValid) {
      onSubmit({ splits });
    }
  };

  const handleDistributeEvenly = () => {
    const amountPerSplit = (totalAmount / splits?.length)?.toFixed(2);
    const newSplits = splits?.map((split, index) => ({
      ...split,
      amount: index === splits?.length - 1
        ? (totalAmount - (amountPerSplit * (splits?.length - 1)))?.toFixed(2)
        : amountPerSplit
    }));
    setSplits(newSplits);
  };

  const getCategoryName = (categoryId) => {
    const category = parentCategories?.find(c => c?.id === categoryId);
    return category?.name || 'Unknown';
  };

  const getSubcategoryName = (splitIndex, subcategoryId) => {
    if (!subcategoryId) return null;
    const subcategory = subcategoriesMap?.[splitIndex]?.find(s => s?.id === subcategoryId);
    return subcategory?.name || null;
  };

  const getAccountName = (accountId) => {
    const account = accounts?.find(a => a?.id === accountId);
    return account?.name || 'Unknown';
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[800px] mx-16 max-h-[90vh] overflow-y-auto">
        <div className="p-20 md:p-24 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div className="flex items-center gap-12">
            <Icon name="Split" size={24} color="var(--color-primary)" />
            <div>
              <h2 className="text-xl font-heading font-semibold text-card-foreground">
                Split Transaction
              </h2>
              <p className="text-sm text-muted-foreground mt-4">
                {transaction?.description}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          <div className="bg-muted rounded-lg p-16 mb-20">
            <div className="flex items-center justify-between mb-8">
              <span className="text-sm font-caption font-medium text-foreground">
                Total Amount:
              </span>
              <span className="text-lg font-heading font-bold text-primary">
                ${totalAmount?.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between mb-8">
              <span className="text-sm font-caption font-medium text-foreground">
                Allocated:
              </span>
              <span className="text-sm font-semibold text-foreground">
                ${allocatedAmount?.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-caption font-medium text-foreground">
                Remaining:
              </span>
              <span className={`text-sm font-semibold ${
                Math.abs(remainingAmount) < 0.01 ? 'text-success' : 'text-error'
              }`}>
                ${remainingAmount?.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between mb-16">
            <h3 className="text-base font-heading font-semibold text-card-foreground">
              Split Allocations ({splits?.length})
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Percent"
              iconPosition="left"
              onClick={handleDistributeEvenly}
            >
              Distribute Evenly
            </Button>
          </div>

          <div className="space-y-12">
            {splits?.map((split, index) => (
              <div key={index} className="border border-border rounded-lg p-16 bg-card hover:shadow-elevation-2 transition-smooth">
                <div className="flex items-center justify-between mb-12">
                  <div className="flex items-center gap-8">
                    <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">{index + 1}</span>
                    </div>
                    <span className="text-sm font-caption font-semibold text-foreground">
                      Split {index + 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-8">
                    {!split?.editing && split?.category && split?.amount && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        iconName="Edit"
                        onClick={() => handleEditSplit(index)}
                      />
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      iconName="Trash2"
                      onClick={() => handleRemoveSplit(index)}
                      disabled={splits?.length <= 2}
                    />
                  </div>
                </div>
                
                {split?.editing || !split?.category || !split?.amount ? (
                  <div className="space-y-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                      <div>
                        <label className="block text-sm font-caption font-medium text-foreground mb-8">
                          Category *
                        </label>
                        <Select
                          options={categoryOptions}
                          value={split?.category}
                          onChange={(value) => handleSplitChange(index, 'category', value)}
                          searchable
                          required
                        />
                      </div>
                      
                      {split?.category && subcategoriesMap?.[index]?.length > 0 && (
                        <div>
                          <label className="block text-sm font-caption font-medium text-foreground mb-8">
                            Subcategory
                          </label>
                          <Select
                            options={getSubcategoryOptions(index)}
                            value={split?.subcategory || ''}
                            onChange={(value) => handleSplitChange(index, 'subcategory', value)}
                            searchable
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                      <div>
                        <label className="block text-sm font-caption font-medium text-foreground mb-8">
                          Amount *
                        </label>
                        <Input
                          type="number"
                          step="0.01"
                          value={split?.amount}
                          onChange={(e) => handleSplitChange(index, 'amount', e?.target?.value)}
                          placeholder="0.00"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-caption font-medium text-foreground mb-8">
                          Account *
                        </label>
                        <Select
                          options={accountOptions}
                          value={split?.account}
                          onChange={(value) => handleSplitChange(index, 'account', value)}
                          searchable
                          required
                        />
                      </div>
                    </div>
                    
                    {split?.category && split?.amount && split?.account && (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          iconName={savingIndex === index ? "Loader2" : "Check"}
                          iconPosition="left"
                          onClick={() => handleSaveSplit(index)}
                          disabled={savingIndex === index}
                        >
                          {savingIndex === index ? 'Saving...' : 'Save Split'}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-muted/50 rounded-lg p-12 space-y-8">
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <p className="text-xs text-muted-foreground mb-4">Category</p>
                        <p className="text-sm font-semibold text-foreground">
                          {getCategoryName(split?.category)}
                          {getSubcategoryName(index, split?.subcategory) && (
                            <span className="text-muted-foreground font-normal"> / {getSubcategoryName(index, split?.subcategory)}</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-4">Amount</p>
                        <p className="text-lg font-bold text-primary">${parseFloat(split?.amount)?.toFixed(2)}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-4">Account</p>
                      <p className="text-sm font-semibold text-foreground">{getAccountName(split?.account)}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddSplit}
            className="w-full mt-16"
          >
            Add Another Split
          </Button>

          <div className="flex gap-12 mt-24 pt-20 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid}
              className="flex-1"
            >
              Save Split Transaction
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SplitTransactionModal;